// The menu-test composite: delivery filter → menu → sections → products → comments,
// mounted by the coffee host with one renderMNBlock call. React owns the block state and the
// report-param grammar (useMenuTestState); the host receives the api on mount and performs the
// param writes, the re-fetches and the export/filters dispatch.

import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isTraitType, resolvePercentDecimals } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

import { calcCommentsProps } from '@aytm/app/stats/page/question-views/mn/components/comments-section/lib/props/block'
import { downloadMenuChart } from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/export'
import { getTraitValues } from '@aytm/app/stats/page/question-views/mn/lib/chart/traitValues'
import { getDeliveryOptions } from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'

import useMenuTestState from '@aytm/app/stats/page/question-views/mn/states/useMenuTestState'

import DeliveryControls from '@aytm/app/stats/page/question-views/mn/components/controls/DeliveryControls'
import MenuSection from '@aytm/app/stats/page/question-views/mn/components/menu-section/MenuSection'
import SectionsSection from '@aytm/app/stats/page/question-views/mn/components/sections-section/SectionsSection'
import ProductsSection from '@aytm/app/stats/page/question-views/mn/components/products-section/ProductsSection'
import CommentsBlock from '@aytm/app/stats/page/question-views/mn/components/comments-section/CommentsBlock'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import EchoScope from '@aytm/app/stats/page/question-views/components/EchoScope'
import Preloader from '@aytm/app/stats/page/question-views/components/Preloader'
import { calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// block_base.renderChart parity: the chart areas hide under a DECLARED map/none mode —
// question model or global — not under the cm selection (D10)
const Block = props => {
  const {
    // question data
    id,
    questionId,
    questionKey,
    qType,
    components,
    format,
    answer,
    answerVariants,
    sections,
    deliveryOptions,
    image,
    priceTemplate,
    na,
    dimensions,
    disabled,
    position,
    link,
    isReportXpert,
    modelDefaults,
    question,
    labelTarget,
    // raw params (React parses)
    params,
    paramsRevision,
    // page context
    defaultSortOrder,
    reportPercentDecimals,
    modelChartMode,
    globalChartMode,
    globalZoomLevel,
    trait,
    traitName,
    ignoreAmount,
    ignorePercents,
    colorTheme,
    chartLegendMode,
    confidenceLevel,
    significanceLevel,
    confidenceLevels,
    features,
    viewClickable,
    isAnimated,
    // the surfaces the comments block reports back through, and the writes it announces
    handlers,
    onCommentsParamsChange,
    onCommentsWcParamsChange,
    onUpdateData,
    // callbacks (React → coffee)
    onReady,
    onStateChange,
    onLabelClick
  } = props

  const {
    menuViewMode,
    menuCollapsed,
    sectionsViewMode,
    sectionsCollapsed,
    productViewMode,
    productsCollapsed,
    selectedDeliveryOptions,
    chartMode,
    zoomLevel,
    sortOrder,
    unCollapsedSections,
    loading,
    onMenuModelChange,
    onSectionsModelChange,
    onProductsModelChange,
    onMenuToggle,
    onSectionsToggle,
    onProductsToggle,
    onDeliveryChange,
    onSortChange,
    onChartModeChange,
    onZoomChange,
    onSectionCardToggle,
    onSectionCardsToggleAll,
    onProductsChartReady,
    onCommentsReady,
    onCommentsStateChange,
    onCommentsWcParams,
    onCommentsCollapsedChange
  } = useMenuTestState({
    id,
    image,
    components,
    params,
    paramsRevision,
    format,
    question,
    deliveryOptions,
    sections,
    answerVariants,
    defaultSortOrder,
    modelChartMode,
    globalChartMode,
    globalZoomLevel,
    answer,
    ignoreAmount,
    ignorePercents,
    confidenceLevels,
    labelTarget,
    onReady,
    onStateChange,
    onCommentsParamsChange,
    onCommentsWcParamsChange,
    onUpdateData
  })

  const traitType = isTraitType({ ...question, format })
  const percentDecimals = resolvePercentDecimals(format, reportPercentDecimals)

  // the values the breakdown draws a row per; the charts resolve their own the same way
  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  // a clicked column names the order it wants; the state settles on one, the host writes it
  const onSort = useCallback(
    (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) onSortChange(order)
    },
    [onSortChange]
  )

  const onProductsLabelClick = useCallback(
    (event, position) => onLabelClick?.(event, position, 'products'),
    [onLabelClick]
  )
  const onSectionsLabelClick = useCallback(
    (event, position) => onLabelClick?.(event, position, 'sections'),
    [onLabelClick]
  )

  // in-view heatmap download, fully React-side (D8) — no host round-trip
  const onMenuExport = useCallback(
    fileType => downloadMenuChart(fileType, { id, image, answer, sections, menuViewMode, questionKey }),
    [id, image, answer, sections, menuViewMode, questionKey]
  )

  // The props the comments block draws from. It is a question of its own as far as the report
  // params go, so it announces its own changes and this block forwards them to the shell.
  const commentsProps = useMemo(
    () =>
      calcCommentsProps({
        id,
        questionId,
        questionKey,
        type: question?.type,
        variant: question?.variant,
        position,
        format,
        components,
        answer,
        answerVariants,
        dimensions,
        disabled,
        clickable: viewClickable,
        questionChartMode: modelChartMode,
        defaultSortOrder,
        modelDefaults,
        batch: labelTarget?.batch,
        link,
        isReportXpert,
        params,
        handlers: {
          ...handlers,
          onCommentsReady,
          onCommentsStateChange,
          onCommentsWcParamsChange: onCommentsWcParams,
          onCommentsCollapsedChange
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      questionId,
      questionKey,
      question,
      position,
      format,
      components,
      answer,
      answerVariants,
      dimensions,
      disabled,
      viewClickable,
      modelChartMode,
      defaultSortOrder,
      modelDefaults,
      labelTarget,
      link,
      isReportXpert,
      params,
      handlers,
      onCommentsReady,
      onCommentsStateChange,
      onCommentsWcParams,
      onCommentsCollapsedChange
    ]
  )

  // the trait props the coffee chart wrappers derived per render (resolveTrait + root flags)
  // two charts hang off one question, so each wrapper is named after its own part
  const chartIdOf = part => (null == id ? undefined : `${id}_${part}`)

  const chartContext = {
    questionKey,
    qType,
    sortOrder,
    chartMode: chartMode || modelChartMode,
    globalChartMode,
    zoomLevel,
    globalZoomLevel,
    traitName,
    trait,
    priceTemplate,
    ignoreTraits: true === format?.ignoreTraits || !traitType || false,
    // the shares the charts leave out: the question's own amounts-only, or the block's flag
    ignorePercents: calcIgnorePercents(format, ignorePercents),
    colorTheme,
    chartLegendMode,
    confidenceLevel,
    significanceLevel,
    confidenceLevels,
    features,
    onChartModeChange,
    onZoomChange
  }

  const tableContext = {
    id,
    answer,
    priceTemplate,
    format,
    trait,
    traitName,
    traitValues,
    isTraitType: traitType,
    viewClickable,
    percentDecimals,
    sortOrder,
    onSort
  }

  return (
    <BlockWrapper {...props}>
      {2 <= (deliveryOptions?.length || 0) && (
        <DeliveryControls
          options={getDeliveryOptions(deliveryOptions)}
          value={selectedDeliveryOptions}
          disabled={Boolean(loading)}
          onChange={onDeliveryChange}
        />
      )}
      {isComponentOn(components, 'menuBlock') && (
        <EchoScope name="menu">
          <MenuSection
            id={id}
            components={components}
            answer={answer}
            format={format}
            image={image}
            sections={sections}
            priceTemplate={priceTemplate}
            na={na}
            isAnimated={isAnimated}
            menuViewMode={menuViewMode}
            menuCollapsed={menuCollapsed}
            chartMode={modelChartMode}
            globalChartMode={globalChartMode}
            onModelChange={onMenuModelChange}
            onToggle={onMenuToggle}
            onExport={onMenuExport}
          />
        </EchoScope>
      )}
      {isComponentOn(components, 'sectionsBlock') && (
        <EchoScope name="sections">
          <SectionsSection
            id={id}
            components={components}
            answer={answer}
            format={format}
            image={image}
            sections={sections}
            priceTemplate={priceTemplate}
            isAnimated={isAnimated}
            sectionsViewMode={sectionsViewMode}
            sectionsCollapsed={sectionsCollapsed}
            chartMode={modelChartMode}
            globalChartMode={globalChartMode}
            unCollapsedSections={unCollapsedSections}
            tableProps={{ ...tableContext, sections, viewMode: sectionsViewMode, onLabelClick: onSectionsLabelClick }}
            chartProps={{ ...chartContext, questionId: chartIdOf('sections') }}
            onModelChange={onSectionsModelChange}
            onToggle={onSectionsToggle}
            onSectionCardToggle={onSectionCardToggle}
            onSectionCardsToggleAll={onSectionCardsToggleAll}
          />
        </EchoScope>
      )}
      {isComponentOn(components, 'productsBlock') && (
        <EchoScope name="products">
          <ProductsSection
            components={components}
            answer={answer}
            answerVariants={answerVariants}
            isAnimated={isAnimated}
            productViewMode={productViewMode}
            productsCollapsed={productsCollapsed}
            chartMode={modelChartMode}
            globalChartMode={globalChartMode}
            tableProps={{
              ...tableContext,
              answerVariants,
              viewMode: productViewMode,
              onLabelClick: onProductsLabelClick
            }}
            chartProps={{ ...chartContext, questionId: chartIdOf('products') }}
            onModelChange={onProductsModelChange}
            onToggle={onProductsToggle}
            onChartReady={onProductsChartReady}
          />
        </EchoScope>
      )}
      {isComponentOn(components, 'commentsBlock') && (
        <EchoScope name="comments">
          <CommentsBlock {...commentsProps} />
        </EchoScope>
      )}
      {/* the updating overlay covers the block while a delivery re-fetch is in flight (D7) */}
      {loading ? <Preloader /> : null}
    </BlockWrapper>
  )
}

Block.displayName = 'MenuTestBlock'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string,
  components: PropTypes.object,
  format: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  sections: PropTypes.array,
  deliveryOptions: PropTypes.array,
  image: PropTypes.object,
  priceTemplate: PropTypes.string,
  na: PropTypes.bool,
  dimensions: PropTypes.object,
  disabled: PropTypes.bool,
  position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  modelDefaults: PropTypes.object,
  question: PropTypes.object,
  labelTarget: PropTypes.object,
  params: PropTypes.object,
  // bumped by the shell on every host render, so a revert to the params last carried still re-seeds
  paramsRevision: PropTypes.number,
  defaultSortOrder: PropTypes.string,
  reportPercentDecimals: PropTypes.number,
  modelChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  globalZoomLevel: PropTypes.string,
  trait: PropTypes.object,
  traitName: PropTypes.string,
  ignoreAmount: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  viewClickable: PropTypes.bool,
  isAnimated: PropTypes.bool,
  handlers: PropTypes.object,
  onCommentsParamsChange: PropTypes.func,
  onCommentsWcParamsChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onLabelClick: PropTypes.func
}

export default Block
